import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { Calendar, User, Tag, ArrowLeft, Loader2 } from 'lucide-react';
import { Badge } from '../components/ui/badge';
import AdBanner from '../components/AdBanner';
import Breadcrumb from '../components/Breadcrumb';
import axios from 'axios';
import { Helmet } from 'react-helmet-async';
import { sanitizeHtml } from '../lib/sanitize';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const BlogPostPage = () => {
  const { slug } = useParams();
  const [post, setPost] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchPost();
  }, [slug]);

  const fetchPost = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API}/blog/posts/${slug}`);
      setPost(response.data);
    } catch (error) {
      console.error('Error fetching post:', error);
      setPost(null);
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-zinc-800 flex items-center justify-center">
        <Loader2 className="w-8 h-8 text-emerald-500 animate-spin" />
      </div>
    );
  }

  if (!post) {
    return (
      <div className="min-h-screen bg-zinc-800 flex items-center justify-center">
        <div className="text-center">
          <p className="text-zinc-400 text-lg mb-4">Post not found</p>
          <Link to="/blog" className="text-emerald-400 hover:text-emerald-300">
            Back to Blog
          </Link>
        </div>
      </div>
    );
  }

  const seo = post.seo || {};
  const pageTitle = seo.title || post.title || 'Blog Post';

  return (
    <>
      <Helmet>
        <title>{`${pageTitle} - Blog`}</title>
        <meta name="description" content={seo.description || post.excerpt || ''} />
        {(seo.keywords || post.tags?.length > 0) && (
          <meta name="keywords" content={seo.keywords || post.tags?.join(', ')} />
        )}
        <meta property="og:title" content={seo.og_title || pageTitle} />
        <meta property="og:description" content={seo.og_description || post.excerpt || ''} />
        <meta property="og:type" content="article" />
        <meta property="og:url" content={window.location.href} />
        {(seo.og_image || post.featured_image) && <meta property="og:image" content={seo.og_image || post.featured_image} />}
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={seo.og_title || pageTitle} />
        <link rel="canonical" href={seo.canonical_url || window.location.href} />
        <script type="application/ld+json">{JSON.stringify({
          "@context": "https://schema.org",
          "@type": "Article",
          "headline": post.title,
          "description": post.excerpt || '',
          "datePublished": post.created_at,
          "dateModified": post.updated_at || post.created_at,
          "author": { "@type": "Organization", "name": "" },
          ...(post.featured_image && { "image": post.featured_image }),
          "publisher": {
            "@type": "Organization",
            "name": ""
          }
        })}</script>
      </Helmet>

      <div className="min-h-screen bg-zinc-800">
        {/* Top Banner Ad */}
        <AdBanner position="top" />
        
        {/* Breadcrumb */}
        <Breadcrumb items={[
          { label: 'Blog', href: '/blog' },
          { label: post.title, href: null }
        ]} />

        {/* Hero Section */}
        <div className="border-b border-zinc-800 py-12 bg-gradient-to-b from-zinc-900 to-black">
          <div className="container mx-auto px-4 text-center">
            <h1 className="text-4xl md:text-5xl font-bold text-white mb-4">{post.title}</h1>
            <p className="text-zinc-400 text-lg max-w-2xl mx-auto">
              {post.excerpt}
            </p>
          </div>
        </div>

        {/* Featured Image */}
        {post.featured_image && (
          <div className="w-full h-96 bg-zinc-900">
            <img 
              src={post.featured_image} 
              alt={post.title}
              className="w-full h-full object-cover"
            />
          </div>
        )}

        {/* Content */}
        <article className="container mx-auto px-4 py-12">
          <div className="max-w-3xl mx-auto">
            {/* Meta */}
            <div className="flex flex-wrap items-center gap-4 text-sm text-zinc-500 mb-6">
              <span className="flex items-center gap-1">
                <Calendar className="w-4 h-4" />
                {formatDate(post.created_at)}
              </span>
              <span className="flex items-center gap-1">
                <User className="w-4 h-4" />
                {post.author_name}
              </span>
              {post.category && (
                <Link to={`/blog?category=${post.category}`}>
                  <Badge className="bg-emerald-500/20 text-emerald-400 hover:bg-emerald-500/30">
                    {post.category}
                  </Badge>
                </Link>
              )}
            </div>

            {/* Title */}
            <h1 className="text-4xl md:text-5xl font-bold text-white mb-6">
              {post.title}
            </h1>

            {/* Excerpt */}
            <p className="text-xl text-zinc-400 mb-8 leading-relaxed">
              {post.excerpt}
            </p>

            {/* Tags */}
            {post.tags && post.tags.length > 0 && (
              <div className="flex flex-wrap gap-2 mb-8">
                {post.tags.map((tag, index) => (
                  <span 
                    key={index}
                    className="text-sm text-zinc-400 bg-zinc-800 px-3 py-1 rounded-full"
                  >
                    #{tag}
                  </span>
                ))}
              </div>
            )}

            {/* Content */}
            <div 
              className="prose prose-invert prose-lg max-w-none
                prose-headings:text-white
                prose-p:text-zinc-300
                prose-a:text-emerald-400 prose-a:no-underline hover:prose-a:underline
                prose-strong:text-white
                prose-ul:text-zinc-300
                prose-ol:text-zinc-300
                prose-blockquote:border-emerald-500 prose-blockquote:text-zinc-400
                prose-code:text-emerald-400 prose-code:bg-zinc-800 prose-code:px-1 prose-code:rounded
                prose-pre:bg-zinc-900 prose-pre:border prose-pre:border-zinc-800
                prose-img:rounded-lg
              "
              dangerouslySetInnerHTML={{ __html: sanitizeHtml(post.content) }}
            />

            {/* Share */}
            <div className="border-t border-zinc-800 mt-12 pt-8">
              <p className="text-zinc-500 text-sm">Share this article</p>
              <div className="flex gap-4 mt-3">
                <a 
                  href={`https://twitter.com/intent/tweet?text=${encodeURIComponent(post.title)}&url=${encodeURIComponent(window.location.href)}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-zinc-400 hover:text-sky-400 transition-colors"
                >
                  Twitter
                </a>
                <a 
                  href={`https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(window.location.href)}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-zinc-400 hover:text-blue-400 transition-colors"
                >
                  Facebook
                </a>
                <a 
                  href={`https://www.linkedin.com/shareArticle?mini=true&url=${encodeURIComponent(window.location.href)}&title=${encodeURIComponent(post.title)}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-zinc-400 hover:text-blue-500 transition-colors"
                >
                  LinkedIn
                </a>
              </div>
            </div>
          </div>
        </article>
      </div>
    </>
  );
};

export default BlogPostPage;
